import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CircleNotch } from "@phosphor-icons/react";

export const TIPOS_EMBALAJE = ["CAJA", "UNIDAD", "BOLSA", "PACK", "OTRO"];

const EMPTY = {
  codigo: "",
  tipo: "",
  nombre: "",
  categoria: "",
  descripcion: "",
  stock_minimo: "",
  unidades_por_embalaje: "",
  tipo_embalaje: "",
  origen: "nacional",
};

export default function ProductoForm({ open, onOpenChange, producto, onSaved }) {
  const isEdit = Boolean(producto);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(
      producto
        ? {
            codigo: producto.codigo,
            tipo: producto.tipo,
            nombre: producto.nombre,
            categoria: producto.categoria || "",
            descripcion: producto.descripcion || "",
            stock_minimo: producto.stock_minimo ?? "",
            unidades_por_embalaje: producto.unidades_por_embalaje ?? "",
            tipo_embalaje: producto.tipo_embalaje || "",
            origen: producto.origen || "nacional",
          }
        : EMPTY
    );
  }, [open, producto]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload = {
      ...form,
      stock_minimo:
        form.stock_minimo === "" || form.stock_minimo === null
          ? null
          : parseInt(form.stock_minimo, 10),
      unidades_por_embalaje:
        form.unidades_por_embalaje === "" || form.unidades_por_embalaje === null
          ? null
          : parseInt(form.unidades_por_embalaje, 10),
      tipo_embalaje: form.tipo_embalaje || null,
    };
    try {
      if (isEdit) {
        await api.put(`/productos/${producto.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/productos", payload);
        toast.success("Producto creado");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="producto-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
            {isEdit ? "Editar producto" : "Nuevo producto"}
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Define los datos del producto. Luego podrás asociarle uno o varios lotes.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Código</Label>
              <Input data-testid="producto-codigo" value={form.codigo} onChange={set("codigo")} required placeholder="ENV-001" className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Tipo</Label>
              <Input data-testid="producto-tipo" value={form.tipo} onChange={set("tipo")} required placeholder="PET, Vidrio..." className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Nombre</Label>
            <Input data-testid="producto-nombre" value={form.nombre} onChange={set("nombre")} required placeholder="Envase 500ml PET" className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Categoría</Label>
            <Input data-testid="producto-categoria" value={form.categoria} onChange={set("categoria")} placeholder="Ej: Botellas, Tapas, Films..." className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Descripción</Label>
            <Textarea data-testid="producto-descripcion" value={form.descripcion} onChange={set("descripcion")} placeholder="Detalles del producto..." className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">
              Punto de reorden (stock mínimo)
            </Label>
            <Input
              type="number"
              min="0"
              data-testid="producto-stock-minimo"
              value={form.stock_minimo}
              onChange={set("stock_minimo")}
              placeholder="Ej: 150 (vacío = umbral global)"
              className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
            />
            <p className="text-xs text-[#94A3B8]">
              Cuando el stock total caiga a este nivel, el producto aparecerá en alertas y se avisará a compras.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Unidades por embalaje</Label>
              <Input
                type="number"
                min="1"
                data-testid="producto-unidades-embalaje"
                value={form.unidades_por_embalaje}
                onChange={set("unidades_por_embalaje")}
                placeholder="Ej: 100"
                className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Tipo de embalaje</Label>
              <Select value={form.tipo_embalaje} onValueChange={setVal("tipo_embalaje")}>
                <SelectTrigger data-testid="producto-tipo-embalaje" className="bg-white border-[#E2E8F0]">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_EMBALAJE.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Origen</Label>
            <Select value={form.origen} onValueChange={setVal("origen")}>
              <SelectTrigger data-testid="producto-origen" className="bg-white border-[#E2E8F0]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nacional">Nacional</SelectItem>
                <SelectItem value="internacional">Internacional</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error && (
            <p data-testid="producto-form-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">{error}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="producto-form-cancel">Cancelar</Button>
            <Button type="submit" disabled={loading} data-testid="producto-form-submit" className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : isEdit ? "Guardar" : "Crear producto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
